/**
 * Rettifica di un SAL già approvato e fatturato.
 *
 * Una fattura emessa non si cancella: si corregge con una nota di credito (o,
 * se si era fatturato meno del dovuto, con una fattura integrativa). Qui si
 * indicano i valori giusti; il sistema mette in coda in Fatturazione il
 * documento per la differenza e conserva il prima e il dopo, con il motivo.
 *
 * Solo staff: si decidono importi verso il cliente.
 */
import { useEffect, useState } from 'react';
import { salApi } from '../api';
import Modal from './Modal';
import CampoImporto from './CampoImporto';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const oggiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function RettificaSal({ salId, onClose, onFatto }) {
  const [d, setD] = useState(null);
  const [errore, setErrore] = useState('');
  const [campi, setCampi] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    salApi.get(salId).then(x => {
      setD(x);
      setCampi({
        percentuale: x.percentuale ?? '',
        importo_costo: Number(x.importo_costo) || 0,
        importo_fattura: Number(x.importo_fattura) || 0,
        motivo: '',
        data: oggiIso(),
      });
    }).catch(e => setErrore(String(e)));
  }, [salId]);

  if (errore) return <Modal title="Rettifica rendiconto" onClose={onClose} size="md"><p className="text-sm text-red-600">{errore}</p></Modal>;
  if (!d || !campi) return <Modal title="Rettifica rendiconto" onClose={onClose} size="md"><p className="text-sm text-gray-400">Caricamento…</p></Modal>;

  const aCorpo = d.modalita_esecuzione !== 'economia';
  const esterno = d.utente_ruolo === 'esterno';
  const q = d.quota_corpo || {};
  const spese = (d.spese || []).reduce((s, x) => s + Number(x.importo || 0), 0);
  const speseCliente = Number(d.proposta_fattura?.spese || 0);

  // A corpo la percentuale giusta ricalcola da sola le due proposte, che
  // restano comunque correggibili: il caso reale può essere una cifra concordata.
  const cambiaPercentuale = (v) => {
    const p = Number(v) || 0;
    setCampi(c => ({
      ...c,
      percentuale: v,
      importo_fattura: q.budget ? +(q.budget * p / 100 + speseCliente).toFixed(2) : c.importo_fattura,
      importo_costo: esterno && q.importo_offerta ? +(q.importo_offerta * p / 100 + spese).toFixed(2) : c.importo_costo,
    }));
  };

  const prima = Number(d.importo_fattura) || 0;
  const delta = +(Number(campi.importo_fattura || 0) - prima).toFixed(2);
  const deltaCosto = +(Number(campi.importo_costo || 0) - (Number(d.importo_costo) || 0)).toFixed(2);

  const conferma = async () => {
    if (!campi.motivo.trim()) return alert('Indica il motivo della rettifica: resta nello storico.');
    if (Math.abs(delta) < 0.01 && Math.abs(deltaCosto) < 0.01 &&
        String(campi.percentuale) === String(d.percentuale ?? '')) return alert('Nessun valore è cambiato.');
    setSalvando(true);
    try {
      await salApi.rettifica(salId, {
        percentuale: aCorpo && campi.percentuale !== '' ? Number(campi.percentuale) : undefined,
        importo_fattura: Number(campi.importo_fattura),
        importo_costo: Number(campi.importo_costo),
        motivo: campi.motivo.trim(),
        data: campi.data,
      });
      onFatto?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  return (
    <Modal title="Rettifica rendiconto già fatturato" onClose={onClose} size="md">
      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
        <div><span className="text-gray-500">Commessa:</span> <strong>{d.commessa_numero} — {d.cliente_nome}</strong></div>
        <div><span className="text-gray-500">Rendiconto:</span> {d.descrizione || '—'} · {d.utente_nome}</div>
        <div className="text-xs text-gray-500 pt-1">
          Valori attuali: {aCorpo && <>avanzamento <strong>{Number(d.percentuale) || 0}%</strong> · </>}
          fatturato <strong>{fmt(d.importo_fattura)}</strong>
          {esterno && <> · credito consulente <strong>{fmt(d.importo_costo)}</strong></>}
        </div>
      </div>

      <div className="space-y-3">
        {aCorpo && (
          <div>
            <label className="label">Avanzamento corretto (%)</label>
            <input className="input" type="number" min="0" max="100" step="0.01"
                   value={campi.percentuale} onChange={e => cambiaPercentuale(e.target.value)} />
            <p className="text-xs text-gray-500 mt-1">
              Ricalcola le proposte qui sotto
              {q.budget ? ` (${fmt(q.budget)} di budget` : ''}
              {esterno && q.importo_offerta ? `, offerta consulente ${fmt(q.importo_offerta)}` : ''}
              {q.budget ? ')' : ''}. Restano correggibili.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Totale fatturabile corretto</label>
            <CampoImporto value={campi.importo_fattura}
                          onChange={v => setCampi(c => ({ ...c, importo_fattura: v }))} />
          </div>
          {esterno && (
            <div>
              <label className="label">Credito consulente corretto</label>
              <CampoImporto value={campi.importo_costo}
                            onChange={v => setCampi(c => ({ ...c, importo_costo: v }))} />
            </div>
          )}
        </div>

        {/* Cosa succede verso il cliente: detto prima di confermare, non dopo. */}
        <div className={`text-sm rounded-lg p-2.5 border ${
          delta < -0.005 ? 'bg-red-50 border-red-200 text-red-800'
          : delta > 0.005 ? 'bg-amber-50 border-amber-200 text-amber-800'
          : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
          {delta < -0.005 && <>Verrà messa in coda in Fatturazione una <strong>nota di credito di {fmt(-delta)}</strong>.</>}
          {delta > 0.005 && <>Verrà messa in coda in Fatturazione una <strong>fattura integrativa di {fmt(delta)}</strong>.</>}
          {Math.abs(delta) <= 0.005 && <>Nessun documento verso il cliente: cambia solo {esterno ? 'il credito del consulente' : 'l\'avanzamento'}.</>}
          {esterno && Math.abs(deltaCosto) >= 0.01 && (
            <div className="text-xs mt-1">
              Credito del consulente: {fmt(d.importo_costo)} → <strong>{fmt(campi.importo_costo)}</strong>
              {' '}({deltaCosto > 0 ? '+' : ''}{fmt(deltaCosto)}).
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="label">Motivo *</label>
            <input className="input" placeholder="es. avanzamento dichiarato 50%, effettivo 30%"
                   value={campi.motivo} onChange={e => setCampi(c => ({ ...c, motivo: e.target.value }))} />
            {esterno && <p className="text-xs text-gray-400 mt-0.5">Visibile anche al consulente.</p>}
          </div>
          <div>
            <label className="label">Data</label>
            <input className="input" type="date" value={campi.data}
                   onChange={e => setCampi(c => ({ ...c, data: e.target.value }))} />
          </div>
        </div>

        {(d.rettifiche || []).length > 0 && (
          <div className="text-xs text-gray-500 border-t pt-2">
            <div className="font-medium text-gray-600 mb-1">Rettifiche precedenti</div>
            {d.rettifiche.map(r => (
              <div key={r.id}>
                {fmtDate(r.data)} · fatturato {fmt(r.fattura_prima)} → {fmt(r.fattura_dopo)} · {r.motivo}
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-gray-400">
          Se la fattura non è ancora stata emessa è più semplice usare <em>Riapri</em>:
          la rettifica serve quando il documento al cliente esiste già.
        </p>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-secondary" onClick={onClose}>Annulla</button>
        <button className="btn-primary" onClick={conferma} disabled={salvando}>
          {salvando ? 'Registro…' : 'Registra rettifica'}
        </button>
      </div>
    </Modal>
  );
}
